import { use } from 'i18next';
import HttpApi, { HttpBackendOptions, RequestCallback } from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { combineReducers } from 'redux';
import { call, put, select } from 'redux-saga/effects';

import { takeLeading } from '@redux-saga/core/effects';
import {
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { selectIsUserInternal } from 'saga/auth/ducks';
import { selectProperty } from 'saga/property/ducks';
import { environment } from 'environments/environment';
import { apiClient } from 'helpers/apiClient';

/* STATE */
export interface TranslationState {
  initI18NextInitialized: boolean;
}

/* ACTION TYPES */
export enum TranslationActionTypes {
  InitI18Next = '@@Translation/INIT_I18_NEXT',
}

/* ACTIONS */
export const initI18NextActions = createApiActionCreators(TranslationActionTypes.InitI18Next);

/* REDUCERS */
const initialState: TranslationState = {
  initI18NextInitialized: false,
};

const initI18NextInitialized = createReducer(initialState.initI18NextInitialized, {
  [TranslationActionTypes.InitI18Next]: {
    [RequestActionTypes.SUCCESS]: () => true,
  },
});

export default combineReducers<TranslationState>({ initI18NextInitialized });

/* SELECTORS */
export const i18NextInitialized = (state: AppState) => state.translation?.initI18NextInitialized;

/* SAGAS */
function* initI18Next({ payload: locale }: any) {
  const isI18NextInitialized: boolean = yield select(i18NextInitialized);

  if (isI18NextInitialized) {
    return;
  }

  const isInternal: boolean = yield select(selectIsUserInternal);
  const url: string = selectProperty(environment.urlConfig.translate.propertyKey);

  yield call(() =>
    use(initReactI18next)
      .use(HttpApi)
      .init({
        ns: Object.keys(environment.translate),
        defaultNS: environment.appTranslation.defaultNamespace,
        lng: locale,
        fallbackLng: false,
        supportedLngs: environment.appTranslation.supportedLocales,
        interpolation: {
          escapeValue: false,
          prefix: '{',
          suffix: '}',
        },
        load: 'currentOnly',
        backend: {
          loadPath: ([language]: string[]) => {
            return `${url}?lang=${language}&internal=${Boolean(isInternal)}`;
          },
          request: (
            options: HttpBackendOptions,
            url: string,
            payload: unknown | string,
            callback: RequestCallback
          ): void => {
            apiClient
              .get(url)
              .then((result) =>
                callback(null, {
                  status: result.status,
                  data: JSON.stringify(result.data),
                })
              )
              .catch((err) =>
                callback(err, {
                  status: err?.response?.status || 500,
                  data: err?.response?.data || '',
                })
              );
          },
          parse: (data: string, languages: string, namespaces: string) => {
            const getStringValue = (def: string, s?: string | string[]): string => {
              if (Array.isArray(s) && s.length) {
                return getStringValue(s[0], def);
              }

              return (s as string) || def;
            };
            const lang = getStringValue(environment.appTranslation.defaultLocale, languages);
            const ns = getStringValue(environment.appTranslation.defaultNamespace, namespaces);

            return JSON.parse(data)[environment.translate[ns].rootKey]['<ANY>'][lang];
          },
        },
        parseMissingKeyHandler: (key: string) => (environment.production ? '' : key),
        debug: true,
      })
  );
  yield put(initI18NextActions.success());
}

/* EXPORT */
export function* translationSaga() {
  yield takeLeading(createActionType(TranslationActionTypes.InitI18Next, RequestActionTypes.REQUEST), initI18Next);
}
